import { useCallback, useMemo, useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import { fetchRecurringEmailRecipients } from "@/modules/emailManagement/features/RecurringEmailManagement/api/fetchRecurringEmailRecipients/fetchRecurringEmailRecipientsApi";
import { RecurringEmailRecipient } from "@/modules/emailManagement/features/RecurringEmailManagement/api/fetchRecurringEmailRecipients/types";
import { unsubscribeRecurringEmailRecipient } from "@/modules/emailManagement/features/RecurringEmailManagement/api/unsubscribeRecurringEmailRecipient/unsubscribeRecurringEmailRecipientApi";

const DEFAULT_PAGE_SIZE = 10;

function matchesSearchTerm(
  recipient: RecurringEmailRecipient,
  searchTerm: string,
): boolean {
  const needle = searchTerm.trim().toLowerCase();

  if (needle === "") {
    return true;
  }

  return [recipient.name, recipient.email, recipient.companyName].some(
    (value) => value?.toLowerCase().includes(needle),
  );
}

export function useRecurringEmailCard(
  recurringEmail: string,
  label: string,
  onUnsubscribed: () => void,
) {
  const { showNotification } = useNotification();

  const [isExpanded, setIsExpanded] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recipients, setRecipients] = useState<RecurringEmailRecipient[]>([]);
  const [searchTerm, setSearchTermState] = useState("");
  const [requestedPageIndex, setRequestedPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [recipientToUnsubscribe, setRecipientToUnsubscribe] =
    useState<RecurringEmailRecipient | null>(null);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);

  const loadRecipients = useCallback(async () => {
    setHasLoaded(true);
    setIsLoading(true);

    try {
      const response = await fetchRecurringEmailRecipients(recurringEmail);
      setRecipients(response.data);
    } catch {
      // Error handled by axios interceptor
    } finally {
      setIsLoading(false);
    }
  }, [recurringEmail]);

  const toggle = useCallback(() => {
    if (!isExpanded && !hasLoaded) {
      loadRecipients();
    }

    setIsExpanded((prev) => !prev);
  }, [isExpanded, hasLoaded, loadRecipients]);

  const filteredRecipients = useMemo(
    () =>
      recipients.filter((recipient) =>
        matchesSearchTerm(recipient, searchTerm),
      ),
    [recipients, searchTerm],
  );

  const setSearchTerm = useCallback((value: string) => {
    setSearchTermState(value);
    setRequestedPageIndex(0);
  }, []);

  const changePage = useCallback(
    (nextPageIndex: number, nextPageSize: number) => {
      setRequestedPageIndex(nextPageIndex);
      setPageSize(nextPageSize);
    },
    [],
  );

  // Unsubscribing the last row of the last page would otherwise strand the
  // table on an empty page.
  const lastPageIndex = Math.max(
    0,
    Math.ceil(filteredRecipients.length / pageSize) - 1,
  );
  const pageIndex = Math.min(requestedPageIndex, lastPageIndex);

  const pagedRecipients = useMemo(
    () =>
      filteredRecipients.slice(
        pageIndex * pageSize,
        (pageIndex + 1) * pageSize,
      ),
    [filteredRecipients, pageIndex, pageSize],
  );

  const requestUnsubscribe = useCallback(
    (recipient: RecurringEmailRecipient) => {
      setRecipientToUnsubscribe(recipient);
    },
    [],
  );

  const cancelUnsubscribe = useCallback(() => {
    setRecipientToUnsubscribe(null);
  }, []);

  const confirmUnsubscribe = useCallback(async () => {
    if (!recipientToUnsubscribe) return;

    setIsUnsubscribing(true);

    try {
      await unsubscribeRecurringEmailRecipient(
        recurringEmail,
        recipientToUnsubscribe.uuid,
      );

      setRecipients((prev) =>
        prev.filter((recipient) => recipient.uuid !== recipientToUnsubscribe.uuid),
      );
      onUnsubscribed();
      showNotification(
        `${recipientToUnsubscribe.name} unsubscribed from ${label}`,
        "",
        "success",
      );
    } catch {
      // Error handled by axios interceptor
    } finally {
      setIsUnsubscribing(false);
      setRecipientToUnsubscribe(null);
    }
  }, [
    recipientToUnsubscribe,
    recurringEmail,
    label,
    onUnsubscribed,
    showNotification,
  ]);

  return {
    isExpanded,
    toggle,
    isLoading,
    recipients,
    filteredRecipients,
    pagedRecipients,
    pageIndex,
    pageSize,
    changePage,
    searchTerm,
    setSearchTerm,
    recipientToUnsubscribe,
    requestUnsubscribe,
    cancelUnsubscribe,
    confirmUnsubscribe,
    isUnsubscribing,
  };
}